import { ArrowRightLeft, Server } from 'lucide-react';
import { useId, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { api, isApiError, type Incident, type IncidentStatus, type IncidentTimelineEntry, type IncidentTransitionRequest } from '../../api';
import { Callout, Field, Section } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { EntityRef } from '../../components/EntityRef';
import { KeyValue } from '../../components/KeyValue';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { CopyText, EmptyState, Loading } from '../../components/States';
import { useWrite, WriteError } from '../../components/Write';
import { elapsedSeconds, formatDateTime, formatDuration, formatRelative } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { alertLink, EnumBadge, humanize, INCIDENT_TONE, RefreshButton, RunbookLink, SeverityBadge, severityOf, useEnumLabel } from './shared';

const INCIDENT_ORDER: IncidentStatus[] = ['OPEN', 'ACKNOWLEDGED', 'MITIGATING', 'MONITORING', 'RESOLVED'];

/** Mirrors `incident_step_allowed`: forward only, plus MONITORING -> MITIGATING. */
function incidentNextStates(from: IncidentStatus): IncidentStatus[] {
  const f = INCIDENT_ORDER.indexOf(from);
  return INCIDENT_ORDER.filter((to, i) => i > f || (from === 'MONITORING' && to === 'MITIGATING'));
}

/** `ops.incident.acknowledged` -> the status it moved to. */
const STEP_STATUS: Record<string, IncidentStatus> = {
  opened: 'OPEN',
  acknowledged: 'ACKNOWLEDGED',
  mitigating: 'MITIGATING',
  monitoring: 'MONITORING',
  resolved: 'RESOLVED',
};

function TransitionForm({ incident, onDone, onCancel }: { incident: Incident; onDone: () => void; onCancel: () => void }) {
  const { t } = useTranslation('ops');
  const { t: tc } = useTranslation();
  const label = useEnumLabel();
  const next = incidentNextStates(incident.status);
  const id = useId();
  const [to, setTo] = useState<IncidentStatus | ''>(next[0] ?? '');
  const [rootCause, setRootCause] = useState(incident.root_cause ?? '');
  const [resolution, setResolution] = useState(incident.resolution ?? '');
  const write = useWrite((req: IncidentTransitionRequest, opts) => api.transitionIncident(incident.id, req, opts));
  const resolving = to === 'RESOLVED';
  const valid = to !== '' && (!resolving || resolution.trim() !== '');

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    const res = await write.run({ to, root_cause: rootCause.trim() || null, resolution: resolution.trim() || null });
    if (res) onDone();
  };

  return (
    <form onSubmit={submit} className="grid gap-4" aria-labelledby={`${id}-t`}>
      <DialogHeader>
        <DialogTitle id={`${id}-t`}>{t('incidents.move.title')}</DialogTitle>
        <DialogDescription>{t('incidents.move.description')}</DialogDescription>
      </DialogHeader>
      <Field label={t('incidents.move.newStatus')}>
        <Select value={to} onValueChange={(v) => setTo(v as IncidentStatus)} disabled={write.busy}>
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {next.map((s) => (
              <SelectItem key={s} value={s}>
                {label('incidentStatus', s)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      <Field label={`${t('incidents.move.rootCause')} (${resolving ? t('incidents.move.recommended') : t('incidents.move.optional')})`}>
        <Textarea rows={3} value={rootCause} onChange={(e) => setRootCause(e.target.value)} disabled={write.busy} maxLength={2000} />
      </Field>
      <Field label={`${t('incidents.move.resolution')} (${resolving ? t('incidents.move.requiredToResolve') : t('incidents.move.optional')})`}>
        <Textarea rows={3} value={resolution} onChange={(e) => setResolution(e.target.value)} disabled={write.busy} maxLength={2000} required={resolving} />
      </Field>
      {write.state.phase === 'error' ? <WriteError error={write.state.error} /> : null}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel} disabled={write.busy}>
          {tc('action.cancel')}
        </Button>
        <Button type="submit" disabled={write.busy || !valid}>
          {write.busy ? t('incidents.move.saving') : t('incidents.move.submit', { status: to ? label('incidentStatus', to).toLowerCase() : '…' })}
        </Button>
      </DialogFooter>
    </form>
  );
}

function Timeline({ entries }: { entries: IncidentTimelineEntry[] }) {
  const { t } = useTranslation('ops');
  const label = useEnumLabel();
  if (entries.length === 0) return <EmptyState title={t('incidents.detail.timelineEmpty')} />;
  return (
    <ol className="relative space-y-5 before:absolute before:top-2 before:bottom-2 before:left-1.5 before:w-px before:bg-border">
      {entries.map((e, i) => {
        const suffix = e.action.replace(/^ops\.incident\./, '');
        const status = STEP_STATUS[suffix];
        const title = status
          ? status === 'OPEN'
            ? t('incidents.detail.opened')
            : t('incidents.detail.movedTo', { status: label('incidentStatus', status) })
          : humanize(e.action);
        const who = e.actor_name ?? e.actor_email;
        return (
          <li key={`${e.at}/${e.action}/${i}`} className="relative flex gap-4">
            <span className="z-10 mt-1.5 size-3 shrink-0 rounded-full border bg-card" aria-hidden="true" />
            <div className="min-w-0 flex-1 space-y-0.5">
              <div className="flex flex-wrap items-baseline gap-x-2 text-sm">
                <span className="font-medium">{title}</span>
                {who ? <span className="text-muted-foreground">{t('incidents.detail.by', { who })}</span> : null}
                <time dateTime={e.at} className="text-xs text-muted-foreground sm:ml-auto" title={formatDateTime(e.at)}>
                  {formatRelative(e.at)}
                </time>
              </div>
              {e.reason ? (
                <p className="text-sm text-muted-foreground">
                  {t('incidents.detail.reason')}: {e.reason}
                </p>
              ) : null}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

/** `/ops/incidents/:id`: one incident, its timeline, what it affects and the next status. */
export function IncidentPage() {
  const { t } = useTranslation('ops');
  const { id = '' } = useParams();
  const [moving, setMoving] = useState(false);
  const result = useApi((signal) => api.getIncident(id, { signal }), [id]);
  const alerts = useApi((signal) => api.listAlerts({ signal }), []);
  const incident = result.status === 'success' ? result.data.incident : null;
  useDocumentTitle(incident?.title ?? t('incidents.title'));
  const crumbs = [{ label: t('crumb') }, { label: t('incidents.title'), to: '/ops/incidents' }, { label: incident?.title ?? '…' }];

  if (result.status === 'loading') {
    return (
      <>
        <PageHeader title={t('incidents.title')} crumbs={crumbs} />
        <Loading rows={4} />
      </>
    );
  }
  if (result.status === 'error' || !incident) {
    const notFound = isApiError(result.error) && result.error.status === 404;
    return (
      <>
        <PageHeader title={t('incidents.title')} crumbs={crumbs} />
        <DataError
          error={result.error}
          onRetry={notFound ? undefined : result.reload}
          title={notFound ? t('incidents.detail.notFound') : t('incidents.detail.loadFailed')}
        />
      </>
    );
  }

  const known = alerts.status === 'success' ? alerts.data.items : [];
  const canMove = incidentNextStates(incident.status).length > 0;
  const duration = elapsedSeconds(incident.started_at, incident.resolved_at);
  const notRecorded = (key: string) => (
    <span key={key} className="text-muted-foreground">
      {t('incidents.detail.notRecorded')}
    </span>
  );

  return (
    <>
      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-3">
            <SeverityBadge severity={incident.severity} withName />
            <span className="break-words">{incident.title}</span>
          </span>
        }
        crumbs={crumbs}
        subtitle={t('incidents.detail.subtitle', { ago: formatRelative(incident.started_at) })}
        actions={
          <>
            <EnumBadge group="incidentStatus" value={incident.status} tones={INCIDENT_TONE} />
            <RefreshButton onClick={result.reload} />
            {canMove ? (
              <Button size="sm" onClick={() => setMoving(true)}>
                <ArrowRightLeft /> {t('incidents.move.button')}
              </Button>
            ) : null}
          </>
        }
      />
      <div className="grid grid-cols-1 items-start gap-6 xl:grid-cols-2">
        <Section title={t('incidents.detail.tabOverview')}>
          <KeyValue
            columns={2}
            rows={[
              [t('incidents.detail.status'), <EnumBadge key="st" group="incidentStatus" value={incident.status} tones={INCIDENT_TONE} />],
              [t('incidents.detail.duration'), formatDuration(duration)],
              [t('incidents.detail.started'), `${formatDateTime(incident.started_at)} (${formatRelative(incident.started_at)})`],
              [t('incidents.detail.acknowledged'), formatDateTime(incident.acknowledged_at)],
              [t('incidents.detail.resolved'), formatDateTime(incident.resolved_at)],
              [
                t('incidents.detail.owner'),
                <EntityRef
                  key="ow"
                  label={incident.owner_name}
                  sub={incident.owner_email}
                  id={incident.owner}
                  to={incident.owner ? `/admin/staff/${encodeURIComponent(incident.owner)}` : undefined}
                />,
              ],
              [t('incidents.detail.id'), <CopyText key="id" value={incident.id} />],
            ]}
          />
        </Section>

        <Section title={t('incidents.detail.timeline')}>
          <Timeline entries={result.data.timeline} />
        </Section>

        <Section title={t('incidents.detail.relatedAlerts')}>
          {incident.related_alerts.length === 0 ? (
            <EmptyState title={t('incidents.detail.alertsEmpty')} />
          ) : (
            <ul className="divide-y">
              {incident.related_alerts.map((name) => {
                const match = known.find((a) => a.labels.alertname === name);
                return (
                  <li key={name} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2 first:pt-0 last:pb-0">
                    <Link to={alertLink(name)} className="font-mono text-sm font-semibold text-primary hover:underline">
                      {name}
                    </Link>
                    {match ? <SeverityBadge severity={severityOf(match)} /> : null}
                    {match ? <RunbookLink url={match.annotations.runbook_url} /> : null}
                  </li>
                );
              })}
            </ul>
          )}
        </Section>

        <Section title={t('incidents.detail.relatedServices')} description={t('incidents.detail.domainsNote')}>
          {incident.related_services.length === 0 ? (
            <EmptyState title={t('incidents.detail.servicesEmpty')} />
          ) : (
            <ul className="flex flex-wrap gap-2">
              {incident.related_services.map((s) => (
                <li key={s}>
                  <Link
                    to={`/ops/services/${encodeURIComponent(s)}`}
                    className="inline-flex items-center gap-1.5 rounded-full border bg-card px-3 py-1 text-sm font-medium text-primary hover:bg-muted"
                  >
                    <Server className="size-3.5" aria-hidden="true" /> {s}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Section>

        <Section title={t('incidents.detail.tabAnalysis')} className="xl:col-span-2">
          <KeyValue
            rows={[
              [
                t('incidents.detail.rootCause'),
                incident.root_cause ? (
                  <span key="rc" className="whitespace-pre-wrap">
                    {incident.root_cause}
                  </span>
                ) : (
                  notRecorded('rc')
                ),
              ],
              [
                t('incidents.detail.resolution'),
                incident.resolution ? (
                  <span key="res" className="whitespace-pre-wrap">
                    {incident.resolution}
                  </span>
                ) : (
                  notRecorded('res')
                ),
              ],
            ]}
          />
          {!canMove ? <Callout className="mt-4">{t('incidents.move.closed')}</Callout> : null}
        </Section>
      </div>
      <Dialog open={moving} onOpenChange={setMoving}>
        <DialogContent>
          {moving ? (
            <TransitionForm
              key={`${incident.id}/${incident.version}`}
              incident={incident}
              onCancel={() => setMoving(false)}
              onDone={() => {
                setMoving(false);
                result.reload();
              }}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}
